import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../context/I18nContext';
import { useTranslations } from '../../hooks/useTranslations';
import { Lead, Project, Payment } from '../../types';
import { getRevenueAmount } from '../../utils/paymentRevenue';
import Card, { CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Trophy, Award } from 'lucide-react';
import { isDemoMode } from '../../services/dataScope';
import { navigateToProject } from '../../utils/navigationUtils';
import { PAGE_STACK, PAGE_GRID_GAP } from '../../styles/pageLayout';

type PeriodValue = 'month' | 'year' | 'total';

interface HighlightsBoardProps {
  leads: Lead[];
  projects: Project[];
  payments: Payment[];
  period?: PeriodValue;
  /** Which highlight block to render. Default: both side by side (legacy). */
  section?: 'leads' | 'projects' | 'both';
}

interface TopLead {
  lead: Lead;
  projectCount: number;
  totalRevenue: number;
}

interface TopProject {
  project: Project;
  leadName: string;
  revenue: number;
}

interface PodiumItem {
  id: string;
  name: string;
  value: number;
  subtitle?: string;
}

const getPaidAmount = (payment: Payment): number => getRevenueAmount(payment);

interface PodiumListCardProps {
  title: string;
  icon: React.ReactNode;
  podiumItems: PodiumItem[];
  listItems: Array<{
    id: string;
    name: string;
    subtitle: string;
    value: number;
  }>;
  formatValue: (value: number) => string;
  getRankColor: (index: number) => string;
  emptyIcon: React.ReactNode;
  emptyMessage: string;
  badgeColors: string[];
  valueColor?: string;
  congratulationsMessage?: React.ReactNode;
  onItemClick?: (id: string) => void;
}

const PodiumListCard: React.FC<PodiumListCardProps> = ({
  title,
  icon,
  podiumItems,
  listItems,
  formatValue,
  getRankColor,
  emptyIcon,
  emptyMessage,
  badgeColors,
  valueColor = 'text-gray-900',
  congratulationsMessage,
  onItemClick,
}) => {
  const podiumColors = [
    'bg-gradient-to-br from-yellow-400 via-yellow-500 to-yellow-600',
    'bg-gradient-to-br from-gray-300 via-gray-400 to-gray-500',
    'bg-gradient-to-br from-orange-400 via-orange-500 to-orange-600',
  ];

  const podiumIcons = [Trophy, Award, Award];
  const podiumHeights = ['h-32', 'h-28', 'h-24'];

  if (podiumItems.length < 3) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {icon}
            {title}
          </CardTitle>
        </CardHeader>
        <CardContent className="flex-1 flex items-center justify-center">
          <div className="flex flex-col items-center justify-center text-center text-gray-500">
            {emptyIcon}
            <p className="text-sm mt-2">{emptyMessage}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          {icon}
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className={`grid grid-cols-1 md:grid-cols-2 ${PAGE_GRID_GAP} items-stretch`}>
          {/* Podium - Left */}
          <div className="flex flex-col items-center gap-2">
            {congratulationsMessage && (
              <div className="w-full mb-4">
                {congratulationsMessage}
              </div>
            )}
            <div className="flex items-end justify-center gap-4 flex-1">
              {podiumItems.map((item, index) => {
              const Icon = podiumIcons[index];
              const isFirst = index === 0;
              return (
                <div
                  key={item.id}
                  className={`flex flex-col items-center transition-transform hover:scale-105 ${isFirst ? 'order-2' : index === 1 ? 'order-1' : 'order-3'} ${onItemClick ? 'cursor-pointer' : ''}`}
                  onClick={onItemClick ? () => onItemClick(item.id) : undefined}
                  role={onItemClick ? 'button' : undefined}
                  tabIndex={onItemClick ? 0 : undefined}
                  onKeyDown={onItemClick ? (e) => { if (e.key === 'Enter') onItemClick(item.id); } : undefined}
                >
                  <div className={`${podiumColors[index]} ${podiumHeights[index]} w-20 rounded-t-lg flex items-center justify-center shadow-lg mb-2`}>
                    <Icon className="w-8 h-8 text-white" />
                  </div>
                  <div className="text-center max-w-[80px]">
                    <p className="font-bold text-gray-900 text-xs truncate">{item.name}</p>
                    <p className={`text-xs font-semibold mt-1 ${valueColor}`}>
                      {formatValue(item.value)}
                    </p>
                    {item.subtitle && (
                      <p className="text-xs text-gray-500 mt-0.5">{item.subtitle}</p>
                    )}
                  </div>
                </div>
              );
              })}
            </div>
          </div>

          {/* List - Right */}
          <div className="space-y-1.5">
            {listItems.map((item, index) => (
              <div
                key={item.id}
                className={`flex items-center justify-between py-2 px-3 rounded-lg border hover:shadow-sm transition-shadow ${getRankColor(index)} ${onItemClick ? 'cursor-pointer' : ''}`}
                onClick={onItemClick ? () => onItemClick(item.id) : undefined}
                role={onItemClick ? 'button' : undefined}
                tabIndex={onItemClick ? 0 : undefined}
                onKeyDown={onItemClick ? (e) => { if (e.key === 'Enter') onItemClick(item.id); } : undefined}
              >
                <div className="flex items-center gap-2.5 flex-1 min-w-0">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center font-bold text-xs flex-shrink-0 ${badgeColors[index] || badgeColors[badgeColors.length - 1]}`}>
                    {index + 1}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 text-sm truncate">{item.name}</p>
                    <p className="text-xs text-gray-600 truncate">{item.subtitle}</p>
                  </div>
                </div>
                <div className="text-right flex-shrink-0 ml-2">
                  <p className={`font-bold text-sm ${valueColor}`}>
                    {formatValue(item.value)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const HighlightsBoard: React.FC<HighlightsBoardProps> = ({
  leads,
  projects,
  payments,
  period = 'year',
  section = 'both',
}) => {
  const { t } = useI18n();
  const { formatCurrency } = useTranslations();
  const navigate = useNavigate();
  const interactive = !isDemoMode();

  const periodRange = useMemo(() => {
    const now = new Date();
    if (period === 'month') {
      return { start: new Date(now.getFullYear(), now.getMonth(), 1), end: now };
    }
    if (period === 'year') {
      return { start: new Date(now.getFullYear(), 0, 1), end: now };
    }
    return { start: null as Date | null, end: now };
  }, [period]);

  const paidPaymentsInPeriod = useMemo(() => {
    const { start, end } = periodRange;
    return payments.filter((payment) => {
      if (payment.status !== 'paid' || !payment.paymentDate) return false;
      if (!(getPaidAmount(payment) > 0)) return false;
      if (!start) return true;
      const paymentDate = new Date(payment.paymentDate);
      return paymentDate >= start && paymentDate <= end;
    });
  }, [payments, periodRange]);

  // Top leads by paid revenue in period
  const topLeadsByRevenue = useMemo(() => {
    const projectsById = new Map(projects.map((p) => [p.id, p]));
    const leadRevenues = new Map<string, TopLead>();
    const projectsPerLead = new Map<string, Set<string>>();

    paidPaymentsInPeriod.forEach((payment) => {
      const project = projectsById.get(payment.projectId);
      if (!project?.leadId) return;
      const lead = leads.find((l) => l.id === project.leadId);
      if (!lead) return;

      const amount = getPaidAmount(payment);
      const current = leadRevenues.get(lead.id);
      if (current) {
        current.totalRevenue += amount;
      } else {
        leadRevenues.set(lead.id, {
          lead,
          projectCount: 0,
          totalRevenue: amount,
        });
      }

      if (!projectsPerLead.has(lead.id)) projectsPerLead.set(lead.id, new Set());
      projectsPerLead.get(lead.id)!.add(project.id);
    });

    leadRevenues.forEach((entry, leadId) => {
      entry.projectCount = projectsPerLead.get(leadId)?.size || 0;
    });

    return Array.from(leadRevenues.values())
      .sort((a, b) => b.totalRevenue - a.totalRevenue)
      .slice(0, 5);
  }, [leads, projects, paidPaymentsInPeriod]);

  // Top projects by paid revenue in period
  const topProjectsByRevenue = useMemo(() => {
    const projectsById = new Map(projects.map((p) => [p.id, p]));
    const revenueByProject = new Map<string, number>();

    paidPaymentsInPeriod.forEach((payment) => {
      if (!payment.projectId) return;
      const amount = getPaidAmount(payment);
      revenueByProject.set(
        payment.projectId,
        (revenueByProject.get(payment.projectId) || 0) + amount
      );
    });

    const projectRevenues: TopProject[] = [];
    revenueByProject.forEach((revenue, projectId) => {
      const project = projectsById.get(projectId);
      if (!project || revenue <= 0) return;
      const lead = leads.find((l) => l.id === project.leadId);
      projectRevenues.push({
        project,
        leadName: lead?.name || t('dashboard.unknownLead'),
        revenue,
      });
    });

    return projectRevenues
      .sort((a, b) => b.revenue - a.revenue)
      .slice(0, 5);
  }, [projects, leads, t, paidPaymentsInPeriod]);

  // Total paid revenue in period (same base as finances "paid")
  const totalRevenue = useMemo(
    () => paidPaymentsInPeriod.reduce((sum, p) => sum + getPaidAmount(p), 0),
    [paidPaymentsInPeriod]
  );
  // Get best lead and project
  const bestLead = topLeadsByRevenue[0];
  const bestProject = topProjectsByRevenue[0];

  // Calculate percentages
  const bestLeadPercentage = totalRevenue > 0 && bestLead 
    ? (bestLead.totalRevenue / totalRevenue) * 100 
    : 0;
  const bestProjectPercentage = totalRevenue > 0 && bestProject 
    ? (bestProject.revenue / totalRevenue) * 100 
    : 0;

  // Prepare podium items (top 3)
  const leadsPodiumItems: PodiumItem[] = topLeadsByRevenue.slice(0, 3).map(item => ({
    id: item.lead.id,
    name: item.lead.name,
    value: item.totalRevenue,
  }));

  const projectsPodiumItems: PodiumItem[] = topProjectsByRevenue.slice(0, 3).map(item => ({
    id: item.project.id,
    name: item.project.name,
    value: item.revenue,
  }));

  // Prepare list items (all 5)
  const leadsListItems = topLeadsByRevenue.map(item => ({
    id: item.lead.id,
    name: item.lead.name,
    subtitle: `${item.projectCount} ${t('projects.title')}`,
    value: item.totalRevenue,
  }));

  const projectsListItems = topProjectsByRevenue.map(item => ({
    id: item.project.id,
    name: item.project.name,
    subtitle: item.leadName,
    value: item.revenue,
  }));

  const getLeadsRankColor = (index: number) => {
    // Fond vert pour toutes les lignes
    return 'bg-gradient-to-r from-green-50 to-emerald-50 border border-green-100';
  };

  const getProjectsRankColor = (index: number) => {
    // Fond violet pour toutes les lignes
    return 'bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-100';
  };

  // Get period label
  const periodLabel = period === 'month' 
    ? t('dashboard.thisMonth') 
    : period === 'year' 
      ? t('dashboard.thisYear') 
      : t('dashboard.total');

  // Prepare congratulations messages
  const leadsCongratulationsMsg = bestLeadPercentage > 0 && bestLead ? (
    <div className="bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
      <div className="text-2xl">🎉</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-900">
          {t('dashboard.congratulations')}
        </p>
        <p 
          className="text-xs text-gray-700 mt-0.5"
          dangerouslySetInnerHTML={{
            __html: t('dashboard.bestLeadMessage', { 
              name: bestLead.lead.name, 
              percentage: bestLeadPercentage.toFixed(1),
              period: periodLabel
            })
          }}
        />
      </div>
    </div>
  ) : null;

  const projectsCongratulationsMsg = bestProjectPercentage > 0 && bestProject ? (
    <div className="bg-gradient-to-r from-yellow-50 via-amber-50 to-yellow-50 border border-yellow-200 rounded-lg p-3 flex items-center gap-2">
      <div className="text-2xl">🎉</div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-semibold text-gray-900">
          {t('dashboard.congratulations')}
        </p>
        <p 
          className="text-xs text-gray-700 mt-0.5"
          dangerouslySetInnerHTML={{
            __html: t('dashboard.bestProjectMessage', { 
              name: bestProject.project.name, 
              percentage: bestProjectPercentage.toFixed(1),
              period: periodLabel
            })
          }}
        />
      </div>
    </div>
  ) : null;

  // Only show sections if we have at least 3 items (enough for a podium)
  const hasEnoughLeads = topLeadsByRevenue.length >= 3;
  const hasEnoughProjects = topProjectsByRevenue.length >= 3;

  if (section === 'leads' && !hasEnoughLeads) return null;
  if (section === 'projects' && !hasEnoughProjects) return null;
  if (section === 'both' && !hasEnoughLeads && !hasEnoughProjects) return null;

  const leadsCard = hasEnoughLeads ? (
    <PodiumListCard
      title={t('dashboard.topLeads')}
      icon={<Trophy className="w-5 h-5 text-green-500" />}
      podiumItems={leadsPodiumItems}
      listItems={leadsListItems}
      formatValue={formatCurrency}
      getRankColor={getLeadsRankColor}
      emptyIcon={<Trophy className="w-12 h-12 mx-auto opacity-50" />}
      emptyMessage={t('dashboard.noDataYet')}
      badgeColors={['bg-yellow-400 text-yellow-900', 'bg-gray-300 text-gray-700', 'bg-orange-400 text-orange-900', 'bg-green-200 text-green-800', 'bg-green-200 text-green-800']}
      valueColor="text-green-700"
      congratulationsMessage={leadsCongratulationsMsg}
      onItemClick={
        interactive
          ? (leadId) => navigate(`/app/leads?lead=${leadId}`)
          : undefined
      }
    />
  ) : null;

  const projectsCard = hasEnoughProjects ? (
    <PodiumListCard
      title={t('dashboard.topProjects')}
      icon={<Trophy className="w-5 h-5 text-purple-500" />}
      podiumItems={projectsPodiumItems}
      listItems={projectsListItems}
      formatValue={formatCurrency}
      getRankColor={getProjectsRankColor}
      emptyIcon={<Trophy className="w-12 h-12 mx-auto opacity-50" />}
      emptyMessage={t('dashboard.noDataYet')}
      badgeColors={['bg-yellow-400 text-yellow-900', 'bg-gray-300 text-gray-700', 'bg-orange-400 text-orange-900', 'bg-purple-200 text-purple-800', 'bg-purple-200 text-purple-800']}
      valueColor="text-purple-700"
      congratulationsMessage={projectsCongratulationsMsg}
      onItemClick={
        interactive
          ? (projectId) => navigateToProject(navigate, projectId)
          : undefined
      }
    />
  ) : null;

  if (section === 'leads') return leadsCard;
  if (section === 'projects') return projectsCard;

  return (
    <div className={`w-full ${PAGE_STACK}`}>
      <div
        className={`grid grid-cols-1 ${hasEnoughLeads && hasEnoughProjects ? 'lg:grid-cols-2' : 'lg:grid-cols-1'} ${PAGE_GRID_GAP}`}
      >
        {leadsCard}
        {projectsCard}
      </div>
    </div>
  );
};

export default HighlightsBoard;
